import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import Loader from "../Components/Loader";
import ProjectPhotoSlider from "../Components/ProjectPhotoSlider";
import Seo from "../Components/Seo";
import { ContactInfo } from "../Utils/Data";
import { API_BASE, fetchApiJson } from "../config/apiBase";
import { pickLocalizedValue } from "../i18n/I18nContext";
import {
  ABOUT_CAMPAIGN_INDEX,
  CAMPAIGN_IMAGES,
  GALLERY_CAMPAIGN_INDICES,
  campaignMapDirectionsUrl,
  campaignMapEmbedUrl,
  campaignMapPlaceUrl,
  formatCampaignAreaRange,
  formatCampaignStartingPrice,
  pickAtIndices,
  TOTAL_APARTMENTS,
} from "../constants/diyarulKhuzamaCampaign";
import "../diyarul-khuzama-landing.css";

const PROPERTY_ID = "fa517391-c697-43e1-b5c3-d3469ef2eab0";
const FALLBACK_IMAGE = "/img/property-placeholder.svg";
const LANDING_FONT_URL =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";

const WHATSAPP_MSG_EN =
  "Hello, I'm interested in Diyar Al Khuzama Complex in Al Taawun, Al Khobar. Please share availability and pricing.";
const WHATSAPP_MSG_AR =
  "مرحباً، أرغب بالاستفسار عن مجمع ديار الخزامى في حي التعاون بالخبر. يرجى مشاركة التفاصيل والأسعار.";

const copy = {
  en: {
    metaDescription:
      "Diyar Al Khuzama — a full residential building with 58 apartments in Al Taawun, Al Khobar. 3-bedroom layouts, pool, gym, smart access, and premium community amenities. Register with QTECH.",
    badge: "58 Apartments · Full Building",
    heroSubtitle: "A complete residential building in Al Taawun — 58 premium apartments",
    register: "Register Interest",
    whatsapp: "Chat on WhatsApp",
    statApartments: "Apartments",
    statBedrooms: "Bedrooms Each",
    statArea: "Typical Unit",
    statLocation: "Al Khobar",
    statPrice: "Starting From",
    trust1: "58-Unit Residential Building",
    trust2: "Saudi Building Code Compliant",
    trust3: "Resort-Style Shared Amenities",
    trust4: "Prime Al Taawun, Al Khobar",
    aboutEyebrow: "The Building",
    aboutTitle: "An entire building designed for elevated family living",
    aboutLead:
      "Diyar Al Khuzama is not a single unit — it is a full residential building offering 58 apartments, each with practical 3-bedroom layouts and access to shared lifestyle amenities.",
    highlightsEyebrow: "Why Diyar Al Khuzama",
    highlightsTitle: "A building that feels like a private community",
    amenitiesEyebrow: "Amenities",
    amenitiesTitle: "Shared facilities for every resident",
    amenitiesLead:
      "Residents across all 58 apartments enjoy the same premium shared spaces — designed for comfort, wellness, and everyday convenience.",
    unitsEyebrow: "The Building",
    unitsTitle: "58 apartments · one distinguished address",
    unitsLead:
      "Each apartment follows a spacious 3-bedroom layout with family lounge, dining, kitchen, maid and driver rooms, and balcony — ideal for owner-occupiers and investors.",
    buildingLabel: "Residential Building",
    apartmentsLabel: "Total Apartments",
    layoutLabel: "Unit Layout",
    layoutValue: "3 Bedroom Apartments",
    locationEyebrow: "Location",
    locationTitle: "Al Taawun — one of Al Khobar's finest districts",
    locationBody:
      "On King Khalid Road in Al Taawun, the compound offers quick access to the Corniche, shopping, schools, and major Eastern Province highways — while keeping a calm residential atmosphere.",
    galleryEyebrow: "Gallery",
    galleryTitle: "Photos from the project",
    formEyebrow: "Register Interest",
    formTitle: "Get pricing & availability for Diyar Al Khuzama",
    formLead: "Leave your details and our QTECH advisors will contact you with current units, pricing, and viewing options.",
    name: "Full Name",
    phone: "Mobile Number",
    email: "Email Address",
    unitType: "Unit Preference",
    unitAny: "3 Bedroom Apartment",
    message: "Message (optional)",
    submit: "Send Registration",
    sending: "Sending…",
    success: "Thank you! Our team will contact you shortly.",
    failed: "Could not send your request. Please try WhatsApp or call us directly.",
    faqEyebrow: "FAQ",
    faqTitle: "Common questions",
    footerNote: "Marketing & advisory by QTECH — Quality Technologies Real Estate",
    footerRights: "All rights reserved.",
    callUs: "Call us",
    stickyCta: "Register Now",
    presentedBy: "Presented by",
    loading: "Loading project…",
    integrated: "Integrated Residential Compound",
    locationLabel: "Location",
    statusLabel: "Listing",
    forSale: "For Sale",
  },
  ar: {
    metaDescription:
      "مجمع ديار الخزامى — مبنى سكني متكامل يضم 58 شقة في حي التعاون بالخبر. 3 غرف نوم، مسبح، نادي رياضي، ودخول ذكي. سجّل اهتمامك مع كيوتك.",
    badge: "58 شقة · مبنى سكني متكامل",
    heroSubtitle: "مبنى سكني متكامل في حي التعاون — 58 شقة فاخرة",
    register: "سجّل اهتمامك",
    whatsapp: "تواصل عبر واتساب",
    statApartments: "شقة",
    statBedrooms: "غرف لكل وحدة",
    statArea: "مساحة الوحدة",
    statLocation: "الخبر",
    statPrice: "يبدأ من",
    trust1: "مبنى سكني بـ 58 وحدة",
    trust2: "متوافق مع كود البناء السعودي",
    trust3: "مرافق مشتركة بمستوى فندقي",
    trust4: "موقع مميز في حي التعاون",
    aboutEyebrow: "المبنى",
    aboutTitle: "مبنى سكني متكامل لحياة عائلية راقية",
    aboutLead:
      "ديار الخزامى ليس شقة واحدة — بل مبنى سكني كامل يضم 58 شقة، كل منها بتخطيط 3 غرف نوم مع مرافق مشتركة متميزة.",
    highlightsEyebrow: "لماذا ديار الخزامى",
    highlightsTitle: "مبنى يمنحك أجواء مجتمع خاص",
    amenitiesEyebrow: "المرافق",
    amenitiesTitle: "مرافق مشتركة لجميع السكان",
    amenitiesLead:
      "يتمتع سكان الـ 58 شقة بنفس المرافق المشتركة الفاخرة — للراحة والعافية والخدمات اليومية.",
    unitsEyebrow: "المبنى",
    unitsTitle: "58 شقة · عنوان واحد مميز",
    unitsLead:
      "كل شقة بتخطيط 3 غرف نوم مع صالة وغرفة طعام ومطبخ وغرفة خادمة وسائق وبلكونة — للسكن أو الاستثمار.",
    buildingLabel: "مبنى سكني",
    apartmentsLabel: "إجمالي الشقق",
    layoutLabel: "تخطيط الوحدة",
    layoutValue: "شقق 3 غرف نوم",
    locationEyebrow: "الموقع",
    locationTitle: "حي التعاون — من أرقى أحياء الخبر",
    locationBody:
      "على طريق الملك خالد في حي التعاون، يوفر المجمع سهولة الوصول إلى الواجهة البحرية والتسوق والمدارس والطرق الرئيسية في المنطقة الشرقية — مع أجواء سكنية هادئة.",
    galleryEyebrow: "معرض الصور",
    galleryTitle: "صور من المشروع",
    formEyebrow: "سجّل اهتمامك",
    formTitle: "احصل على الأسعار والتوفر لمجمع ديار الخزامى",
    formLead: "اترك بياناتك وسيتواصل معك مستشارو كيوتك بخصوص الوحدات المتاحة والأسعار ومواعيد المعاينة.",
    name: "الاسم الكامل",
    phone: "رقم الجوال",
    email: "البريد الإلكتروني",
    unitType: "نوع الوحدة",
    unitAny: "شقة 3 غرف نوم",
    message: "رسالة (اختياري)",
    submit: "إرسال التسجيل",
    sending: "جارٍ الإرسال…",
    success: "شكراً لك! سيتواصل فريقنا معك قريباً.",
    failed: "تعذر إرسال الطلب. يرجى التواصل عبر واتساب أو الاتصال مباشرة.",
    faqEyebrow: "الأسئلة الشائعة",
    faqTitle: "أسئلة متكررة",
    footerNote: "التسويق والاستشارات عبر كيوتك — تقنيات الجودة للعقارات",
    footerRights: "جميع الحقوق محفوظة.",
    callUs: "اتصل بنا",
    stickyCta: "سجّل الآن",
    presentedBy: "يقدّمه",
    loading: "جاري تحميل المشروع…",
    integrated: "مجمع سكني متكامل",
    locationLabel: "الموقع",
    statusLabel: "العرض",
    forSale: "للبيع",
  },
};

const faqs = {
  en: [
    {
      q: "Where is Diyar Al Khuzama located?",
      a: "The compound is in Al Taawun District, Al Khobar — on King Khalid Road, with easy access to the Corniche and major Eastern Province routes.",
    },
    {
      q: "How many apartments are in the building?",
      a: "Diyar Al Khuzama is a full residential building with 58 apartments. Each unit follows a spacious 3-bedroom layout with shared access to premium amenities.",
    },
    {
      q: "What is the apartment layout?",
      a: "Units feature 3 spacious bedrooms, a family lounge, dining area, kitchen with storage, maid's room, driver room, and balcony — designed for comfortable family living.",
    },
    {
      q: "What amenities are included?",
      a: "Residents enjoy a swimming pool, fully equipped gym, games room, events hall, meeting room, private parking, smart access, and AC throughout the compound.",
    },
    {
      q: "How do I register or schedule a viewing?",
      a: "Complete the form on this page, message us on WhatsApp, or call QTECH directly. Our team will share pricing, availability, and viewing times.",
    },
  ],
  ar: [
    {
      q: "أين يقع مجمع ديار الخزامى؟",
      a: "يقع المجمع في حي التعاون بمدينة الخبر على طريق الملك خالد، مع سهولة الوصول إلى الواجهة البحرية والطرق الرئيسية في المنطقة الشرقية.",
    },
    {
      q: "كم عدد الشقق في المبنى؟",
      a: "ديار الخزامى مبنى سكني متكامل يضم 58 شقة. كل وحدة بتخطيط 3 غرف نوم مع مرافق مشتركة فاخرة.",
    },
    {
      q: "ما تخطيط الشقة؟",
      a: "تتضمن الوحدات 3 غرف نوم واسعة وصالة عائلية وغرفة طعام ومطبخ مع مستودع وغرفة خادمة وغرفة سائق وبلكونة — بما يناسب العائلة.",
    },
    {
      q: "ما المرافق المتوفرة؟",
      a: "يتمتع السكان بمسبح ونادي رياضي مجهز وغرفة ألعاب وقاعة مناسبات وقاعة اجتماعات ومواقف خاصة ودخول ذكي وتكييف.",
    },
    {
      q: "كيف أسجّل أو أحجز معاينة؟",
      a: "املأ النموذج في هذه الصفحة أو راسلنا على واتساب أو اتصل بكيوتك مباشرة.",
    },
  ],
};

function resolvePropertyImage(url, apiBase = API_BASE) {
  if (!url) return FALLBACK_IMAGE;
  if (/^data:image\//i.test(url)) {
    return url.length > 2048 ? `${apiBase}/api/properties/${PROPERTY_ID}/cover` : url;
  }
  if (/^https?:\/\//i.test(url) && /\/(uploads|api)\//i.test(url)) {
    return `${apiBase}${new URL(url).pathname}`;
  }
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/uploads/") || url.startsWith("/api/")) return `${apiBase}${url}`;
  return url.startsWith("/") ? url : `/${url}`;
}

function buildWhatsAppUrl(phone, message) {
  const digits = String(phone || "").replace(/[^\d]/g, "");
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function descriptionParagraphs(text = "") {
  return String(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function splitHeroTitle(title = "") {
  const cleaned = String(title).trim();
  if (!cleaned) return { main: "", location: "" };

  const parts = cleaned.split(/\s*[-–|]\s*/).filter(Boolean);
  if (parts.length <= 1) return { main: cleaned, location: "" };

  return {
    main: parts[0].trim(),
    location: parts
      .slice(1)
      .map((part) => part.replace(/\s+City$/i, "").trim())
      .filter(Boolean)
      .join(" · "),
  };
}

const initialForm = { name: "", phone: "", email: "", unitType: "", message: "" };

export default function DiyarulKhuzamaLanding() {
  const [locale, setLocale] = useState("ar");
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [openFaq, setOpenFaq] = useState(0);

  const t = copy[locale] || copy.ar;
  const isRtl = locale === "ar";
  const faqList = faqs[locale] || faqs.ar;

  const localizedTitle = useMemo(() => {
    if (locale === "ar") return "مجمع ديار الخزامى";
    const fromDb = pickLocalizedValue(property, "title", "en", "");
    return splitHeroTitle(fromDb).main || "Diyar Al Khuzama";
  }, [property, locale]);

  const heroHeadline = localizedTitle;

  const aboutParagraphs = useMemo(() => {
    const desc = pickLocalizedValue(property, "description", locale, "");
    const paras = descriptionParagraphs(desc);
    return paras.length ? paras.slice(0, 4) : [t.locationBody];
  }, [property, locale, t.locationBody]);

  const propertyImages = useMemo(() => {
    const fromDb = (property?.images || [])
      .map((img) => resolvePropertyImage(img?.imageUrl))
      .filter(Boolean);
    if (fromDb.length) return fromDb;
    if (property?.coverImage) return [resolvePropertyImage(property.coverImage)];
    return [FALLBACK_IMAGE];
  }, [property]);

  const campaignImages = CAMPAIGN_IMAGES;
  const heroImage = propertyImages[0];
  const aboutImage = campaignImages[ABOUT_CAMPAIGN_INDEX] || campaignImages[0] || heroImage;
  const gallerySlides = pickAtIndices(campaignImages, GALLERY_CAMPAIGN_INDICES);

  const highlights = useMemo(
    () => [
      {
        icon: <i className="fas fa-building" />,
        title: locale === "ar" ? `${TOTAL_APARTMENTS} شقة` : `${TOTAL_APARTMENTS} Apartments`,
        text:
          locale === "ar"
            ? "مبنى متكامل — للسكن أو الاستثمار."
            : "Full building — ideal for living or investment.",
      },
      {
        icon: <i className="fas fa-home" />,
        title: locale === "ar" ? "3 غرف نوم" : "3 Bedrooms Each",
        text:
          locale === "ar"
            ? "تخطيط عائلي عملي في كل وحدة."
            : "Practical family layout in every unit.",
      },
      {
        icon: <i className="fas fa-swimmer" />,
        title: locale === "ar" ? "مسبح ونادي" : "Pool & Gym",
        text:
          locale === "ar"
            ? "مرافق مشتركة لجميع السكان."
            : "Premium shared facilities for residents.",
      },
      {
        icon: <i className="fas fa-lock" />,
        title: locale === "ar" ? "دخول ذكي" : "Smart Access",
        text:
          locale === "ar"
            ? "مواقف خاصة ودخول آمن."
            : "Private parking and secure entry.",
      },
      {
        icon: <i className="fas fa-map-marker-alt" />,
        title: locale === "ar" ? "حي التعاون" : "Al Taawun District",
        text:
          locale === "ar"
            ? "موقع مميز في الخبر."
            : "Prime Al Khobar address.",
      },
      {
        icon: <i className="fas fa-star" />,
        title: locale === "ar" ? "جودة تشطيب" : "Quality Finishes",
        text:
          locale === "ar"
            ? "تصميم عصري ومساحات مريحة."
            : "Contemporary design, generous spaces.",
      },
    ],
    [locale]
  );

  const priceText = useMemo(() => formatCampaignStartingPrice(locale), [locale]);
  const areaStat = useMemo(() => formatCampaignAreaRange(locale), [locale]);

  const mapEmbed = campaignMapEmbedUrl();
  const mapDirectionsUrl = campaignMapDirectionsUrl();
  const mapPlaceUrl = campaignMapPlaceUrl();

  const locationLine = useMemo(
    () => (locale === "ar" ? "حي التعاون · الخبر" : "Al Taawun · Al Khobar"),
    [locale]
  );

  const whatsappUrl = useMemo(
    () => buildWhatsAppUrl(ContactInfo.contact_phone, locale === "ar" ? WHATSAPP_MSG_AR : WHATSAPP_MSG_EN),
    [locale]
  );

  useLayoutEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
    return () => {
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [locale, isRtl]);

  useEffect(() => {
    const id = "dk-landing-fonts";
    if (document.getElementById(id)) return undefined;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = LANDING_FONT_URL;
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadProperty = async () => {
      setLoading(true);
      try {
        const { json } = await fetchApiJson(`/api/properties/${PROPERTY_ID}?lang=${locale}`);
        const data = json?.data || json;
        if (!ignore && data) setProperty(data);
      } catch {
        if (!ignore) setProperty(null);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadProperty();
    return () => {
      ignore = true;
    };
  }, [locale]);

  const scrollToForm = () => {
    document.getElementById("dk-register")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setFeedback({ type: "", message: "" });

    try {
      const { json } = await fetchApiJson("/api/services/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: [form.unitType ? `Unit: ${form.unitType}` : t.unitAny, form.message].filter(Boolean).join("\n"),
          sourceTitle: `${localizedTitle} — Landing Page`,
          sourceSlug: "diyarul-khuzama",
        }),
      });

      if (!json?.success) throw new Error(json?.message || t.failed);

      setForm(initialForm);
      setFeedback({ type: "success", message: t.success });
    } catch (error) {
      setFeedback({ type: "error", message: error?.message || t.failed });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !property) {
    return (
      <div className="dk-landing dk-landing--loading" dir={isRtl ? "rtl" : "ltr"}>
        <Loader label={t.loading} />
      </div>
    );
  }

  const bedroomStat = property?.bedrooms ? String(property.bedrooms).replace(/\D/g, "") || "3" : "3";

  return (
    <div className="dk-landing" dir={isRtl ? "rtl" : "ltr"}>
      <Seo
        title={`${localizedTitle} | QTECH`}
        description={t.metaDescription}
        path="/diyarul-khuzama"
        image={heroImage.startsWith("http") ? heroImage : undefined}
        noindex={false}
      />

      <header className="dk-landing__topbar">
        <div className="dk-landing__topbar-inner">
          <a href="/" className="dk-landing__brand" aria-label="QTECH Home">
            <img src="/img/final-logo.svg" alt="QTECH" className="dk-landing__brand-logo dk-landing__brand-logo--light" />
            <span className="dk-landing__brand-divider" />
            <span className="dk-landing__brand-project">{heroHeadline}</span>
          </a>
          <div className="dk-landing__topbar-actions">
            <div className="dk-landing__lang" role="group" aria-label="Language">
              <button type="button" className={locale === "ar" ? "is-active" : ""} onClick={() => setLocale("ar")}>
                AR
              </button>
              <button type="button" className={locale === "en" ? "is-active" : ""} onClick={() => setLocale("en")}>
                EN
              </button>
            </div>
            <a href={`tel:${ContactInfo.contact_phone}`} className="dk-landing__phone">
              {ContactInfo.contact_phone}
            </a>
            <button type="button" className="dk-landing__btn dk-landing__btn--gold" onClick={scrollToForm}>
              {t.register}
            </button>
          </div>
        </div>
      </header>

      {/* Static hero — photo slider is in the gallery section below */}
      <section className="dk-landing__hero">
        <div
          className="dk-landing__hero-bg"
          style={{
            backgroundImage: `linear-gradient(120deg, rgba(23, 27, 42, 0.92) 0%, rgba(23, 27, 42, 0.72) 45%, rgba(23, 27, 42, 0.55) 100%), url(${heroImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden="true"
        />
        <div className="dk-landing__hero-overlay" aria-hidden="true" />
        <div className="container dk-landing__hero-grid">
          <div className="dk-landing__hero-copy">
            <span className="dk-landing__badge">{t.badge}</span>
            <p className="dk-landing__presented">{t.presentedBy} QTECH</p>
            <h1 className="dk-landing__hero-title">
              <span className="dk-landing__hero-title-main">{heroHeadline}</span>
            </h1>
            <p className="dk-landing__hero-sub">{t.heroSubtitle}</p>
            <div className="dk-landing__hero-ctas">
              <button type="button" className="dk-landing__btn dk-landing__btn--gold" onClick={scrollToForm}>
                {t.register}
              </button>
              <a href={whatsappUrl} target="_blank" rel="noreferrer" className="dk-landing__btn dk-landing__btn--outline">
                {t.whatsapp}
              </a>
            </div>
            <div className="dk-landing__stats">
              <div>
                <strong>{TOTAL_APARTMENTS}</strong>
                <span>{t.statApartments}</span>
              </div>
              <div>
                <strong>{bedroomStat}</strong>
                <span>{t.statBedrooms}</span>
              </div>
              <div>
                <strong>{areaStat}</strong>
                <span>{t.statArea}</span>
              </div>
              <div>
                <strong>{priceText}</strong>
                <span>{t.statPrice}</span>
              </div>
            </div>
          </div>
          <div className="dk-landing__hero-visual">
            <div className="dk-landing__hero-card">
              <img src={heroImage} alt={localizedTitle} />
              <div className="dk-landing__hero-card-caption">
                <span>{locale === "ar" ? `${TOTAL_APARTMENTS} شقة · ${t.integrated}` : `${TOTAL_APARTMENTS} Apartments · ${t.integrated}`}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dk-landing__trust">
        <div className="container dk-landing__trust-grid">
          {[t.trust1, t.trust2, t.trust3, t.trust4].map((item) => (
            <div key={item} className="dk-landing__trust-item">
              <span className="dk-landing__trust-dot" />
              {item}
            </div>
          ))}
        </div>
      </section>

      <section className="dk-landing__section dk-landing__about">
        <div className="container dk-landing__split">
          <div>
            <p className="dk-landing__eyebrow">{t.aboutEyebrow}</p>
            <h2>{t.aboutTitle}</h2>
            <p className="dk-landing__lead">{t.aboutLead}</p>
            {aboutParagraphs.map((para) => (
              <p key={para.slice(0, 40)}>{para}</p>
            ))}
          </div>
          <div className="dk-landing__about-media">
            <video 
              src="/video/diyar-ul-khuzama.MP4" 
              autoPlay
              muted
              loop
              controls 
              playsInline 
              preload="metadata"
              style={{ objectFit: "cover" }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      <section className="dk-landing__section dk-landing__highlights">
        <div className="container">
          <div className="dk-landing__section-head">
            <p className="dk-landing__eyebrow">{t.highlightsEyebrow}</p>
            <h2>{t.highlightsTitle}</h2>
          </div>
          <div className="dk-landing__card-grid">
            {highlights.map((item) => (
              <article key={item.title} className="dk-landing__card">
                <span className="dk-landing__card-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dk-landing__section dk-landing__units">
        <div className="container">
          <div className="dk-landing__section-head">
            <p className="dk-landing__eyebrow">{t.unitsEyebrow}</p>
            <h2>{t.unitsTitle}</h2>
            <p className="dk-landing__lead">{t.unitsLead}</p>
          </div>
          <div className="dk-landing__building-grid">
            <article className="dk-landing__building-card dk-landing__building-card--feature">
              <span className="dk-landing__unit-tag">{t.buildingLabel}</span>
              <h3>{locale === "ar" ? `${TOTAL_APARTMENTS} شقة سكنية` : `${TOTAL_APARTMENTS} Residential Apartments`}</h3>
              <p className="dk-landing__building-stat">{t.apartmentsLabel}</p>
              <p className="dk-landing__unit-beds">
                {locale === "ar"
                  ? "مبنى كامل للبيع/الاستثمار — ليس شقة واحدة"
                  : "Full building opportunity — not a single apartment listing"}
              </p>
            </article>
            <article className="dk-landing__building-card">
              <span className="dk-landing__unit-tag">{t.layoutLabel}</span>
              <h3>{t.layoutValue}</h3>
              <p className="dk-landing__unit-size">{areaStat}</p>
              <p className="dk-landing__unit-beds">
                {property?.bedrooms ||
                  (locale === "ar" ? "3 غرف + غرفة خادمة + غرفة سائق" : "3 beds + maid + driver room")}
              </p>
            </article>
            <article className="dk-landing__building-card">
              <span className="dk-landing__unit-tag">{t.forSale}</span>
              <h3>{locale === "ar" ? "يبدأ السعر من" : "Starting Price"}</h3>
              <p className="dk-landing__unit-price">{priceText}</p>
              <button type="button" className="dk-landing__btn dk-landing__btn--gold" onClick={scrollToForm}>
                {t.register}
              </button>
            </article>
          </div>
        </div>
      </section>

      <section className="dk-landing__section dk-landing__gallery">
        <div className="container">
          <div className="dk-landing__section-head dk-landing__section-head--center">
            <p className="dk-landing__eyebrow">{t.galleryEyebrow}</p>
            <h2>{t.galleryTitle}</h2>
          </div>
        </div>
        <div className="dk-landing__gallery-showcase">
          <ProjectPhotoSlider
            images={gallerySlides.length ? gallerySlides : propertyImages}
            altPrefix={localizedTitle}
            autoPlayMs={4200}
            variant="showcase"
            isRtl={isRtl}
          />
        </div>
      </section>

      <section className="dk-landing__section dk-landing__location">
        <div className="container dk-landing__split">
          <div>
            <p className="dk-landing__eyebrow">{t.locationEyebrow}</p>
            <h2>{t.locationTitle}</h2>
            <p>{t.locationBody}</p>
            <div className="dk-landing__location-tags">
              <span>{locationLine}</span>
              <span>{locale === "ar" ? "المنطقة الشرقية" : "Eastern Province"}</span>
            </div>
            <a
              href={mapDirectionsUrl}
              target="_blank"
              rel="noreferrer"
              className="dk-landing__btn dk-landing__btn--gold"
              style={{ marginTop: 18, display: "inline-flex" }}
            >
              {locale === "ar" ? "الاتجاهات على خرائط جوجل" : "Get directions on Google Maps"}
            </a>
          </div>
          <div className="dk-landing__map-wrap">
            <iframe title={t.locationTitle} src={mapEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <a
              className="dk-landing__map-open"
              href={mapPlaceUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={locale === "ar" ? "فتح الموقع في خرائط جوجل" : "Open location in Google Maps"}
            />
          </div>
        </div>
      </section>

      <section className="dk-landing__section dk-landing__form-section" id="dk-register">
        <div className="container dk-landing__form-grid">
          <div className="dk-landing__form-copy">
            <p className="dk-landing__eyebrow">{t.formEyebrow}</p>
            <h2>{t.formTitle}</h2>
            <p>{t.formLead}</p>
            <div className="dk-landing__form-contacts">
              <a href={`tel:${ContactInfo.contact_phone}`}>{ContactInfo.contact_phone}</a>
              <a href={`mailto:${ContactInfo.contact_email}`}>{ContactInfo.contact_email}</a>
              <a href={whatsappUrl} target="_blank" rel="noreferrer">
                WhatsApp
              </a>
            </div>
          </div>
          <form className="dk-landing__form" onSubmit={handleSubmit}>
            <label>
              {t.name}
              <input name="name" value={form.name} onChange={handleChange} required autoComplete="name" />
            </label>
            <label>
              {t.phone}
              <input name="phone" type="tel" value={form.phone} onChange={handleChange} required autoComplete="tel" />
            </label>
            <label>
              {t.email}
              <input name="email" type="email" value={form.email} onChange={handleChange} required autoComplete="email" />
            </label>
            <label>
              {t.unitType}
              <input name="unitType" value={form.unitType || t.unitAny} onChange={handleChange} readOnly />
            </label>
            <label>
              {t.message}
              <textarea name="message" rows={4} value={form.message} onChange={handleChange} />
            </label>
            {feedback.message ? (
              <p className={`dk-landing__feedback dk-landing__feedback--${feedback.type}`}>{feedback.message}</p>
            ) : null}
            <button type="submit" className="dk-landing__btn dk-landing__btn--gold dk-landing__btn--block" disabled={submitting}>
              {submitting ? t.sending : t.submit}
            </button>
          </form>
        </div>
      </section>

      <section className="dk-landing__section dk-landing__faq">
        <div className="container">
          <div className="dk-landing__section-head">
            <p className="dk-landing__eyebrow">{t.faqEyebrow}</p>
            <h2>{t.faqTitle}</h2>
          </div>
          <div className="dk-landing__faq-list">
            {faqList.map((item, index) => (
              <div key={item.q} className={`dk-landing__faq-item${openFaq === index ? " is-open" : ""}`}>
                <button type="button" onClick={() => setOpenFaq(openFaq === index ? -1 : index)}>
                  {item.q}
                  <span>{openFaq === index ? "−" : "+"}</span>
                </button>
                {openFaq === index ? <p>{item.a}</p> : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="dk-landing__footer">
        <div className="container dk-landing__footer-inner">
          <img src="/img/final-logo.svg" alt="QTECH" className="dk-landing__footer-logo" />
          <p>{t.footerNote}</p>
          <p className="dk-landing__footer-copy">© {new Date().getFullYear()} QTECH. {t.footerRights}</p>
        </div>
      </footer>

      <div className="dk-landing__sticky">
        <a href={`tel:${ContactInfo.contact_phone}`} className="dk-landing__sticky-call">
          {t.callUs}
        </a>
        <button type="button" className="dk-landing__btn dk-landing__btn--gold" onClick={scrollToForm}>
          {t.stickyCta}
        </button>
        <a href={whatsappUrl} target="_blank" rel="noreferrer" className="dk-landing__sticky-wa" aria-label="WhatsApp">
          <i className="fab fa-whatsapp" />
        </a>
      </div>
    </div>
  );
}
